import { useContext, useEffect, useReducer, useState } from "react";
import { Spinner } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/esm/Modal";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../SignupScreen.css";
import { Store } from "../Store";
import OtpValidationModal from "../components/OtpValidationModal";
import useAxios from "../hooks/useAxios";
import {
  businessTypes,
  capitalize,
  getError,
  loginHandler,
  validatePassword,
} from "../utils";
import PrivacyStatement from "./PrivacyStatement";
import TermsOfService from "./TermsOfService";

const reducer = (state, action) => {
  switch (action.type) {
    case "CREATE_REQUEST":
      return { ...state, loadingCreate: true };
    case "CREATE_SUCCESS":
      return {
        ...state,
        loadingCreate: false,
      };
    case "CREATE_FAIL":
      return { ...state, loadingCreate: false };
    case "FETCH_REQUEST":
      return { ...state, loading: true };
    case "FETCH_SUCCESS":
      return { ...state, loading: false };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "UPDATE_REQUEST":
      return { ...state, loadingUpdate: true };
    case "UPDATE_SUCCESS":
      return { ...state, loadingUpdate: false };
    case "UPDATE_FAIL":
      return { ...state, loadingUpdate: false };
    case "UPLOAD_REQUEST":
      return { ...state, loadingUpload: true, errorUpload: "" };
    case "UPLOAD_SUCCESS":
      return {
        ...state,
        loadingUpload: false,
        errorUpload: "",
      };
    case "UPLOAD_FAIL":
      return { ...state, loadingUpload: false, errorUpload: action.payload };

    default:
      return state;
  }
};

export default function SignupScreen() {
  const Axios = useAxios();
  const navigate = useNavigate();
  const { search } = useLocation();
  const redirectInUrl = new URLSearchParams(search).get("redirect");
  const redirect = redirectInUrl ? redirectInUrl : "/";

  const [
    { loading, error, loadingUpdate, loadingUpload, loadingCreate },
    dispatch,
  ] = useReducer(reducer, {
    loading: false,
    error: "",
    loadingCreate: false,
    loadingUpdate: false,
  });

  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [showOtpValidation, setShowOtpValidation] = useState(false);
  const [otpValidated, setOtpValidated] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyStatement, setShowPrivacyStatement] = useState(false);
  const [otpValidatingStatus, setOtpValidatingStatus] = useState(false);
  const [customerOtp, setCustomerOtp] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formValidated, setFormValidated] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyStatementAccepted, setPrivacyStatementAccepted] =
    useState(false);

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;
  const [passwordStrength, setPasswordStrength] = useState("");

  const submitHandler = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false || !businessType) {
      setFormValidated(true);
      return;
    }

    if (passwordStrength !== "Şifre güçlü") {
      toast.error("Lütfen daha güçlü bir şifre oluşturunuz!");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Şifre tekrarı hatalı");
      return;
    }

    if (!termsAccepted) {
      toast.error("Lütfen 'Kullanım koşulları'nı kabul ediniz!");
      return;
    }
    if (!privacyStatementAccepted) {
      toast.error(
        "Lütfen 'Kişisel verilerimin işlenmesine yönelik Aydınlatma Metni'ni okuyunuz!"
      );
      return;
    }
    dispatch({ type: "CREATE_REQUEST" });

    try {
      const { data } = await Axios.get(`/api/busers/checkemail/${email}`);
      if (data.emailInUse) {
        toast.error(
          "Girdiğiniz e-posta adresi başka bir hesapta kullanılmış, lütfen başka bir e-posta adresi ile kayıt yapınız"
        );
        dispatch({ type: "CREATE_FAIL" });
        return;
      }
    } catch (err) {
      toast.error("E-posta adresiniz kontrol edilirken hata alındı!");
      dispatch({ type: "CREATE_FAIL" });
      return;
    }

    if (!otpValidated) {
      try {
        await Axios.post("/api/email/send-otp", {
          to: email,
          name: name,
        });
        setShowOtpValidation(true);
      } catch (err) {
        toast.error("Doğrulama e-postası gönderilemedi");
        dispatch({ type: "CREATE_FAIL" });
        return;
      }
      dispatch({ type: "CREATE_SUCCESS" });
      return;
    }
    dispatch({ type: "CREATE_SUCCESS" });
  };

  const handleKayitFinish = async () => {
    dispatch({ type: "CREATE_REQUEST" });
    try {
      const { data } = await Axios.post("/api/busers/signup", {
        name,
        email,
        password,
        businessName,
        businessType,
      });
      loginHandler(data, ctxDispatch, localStorage);
      navigate(redirect || "/");
      dispatch({ type: "CREATE_SUCCESS" });
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "CREATE_FAIL" });
    }
  };

  const handleValidateOtp = async (kod) => {
    try {
      const { data } = await Axios.post("/api/email/verify-otp", {
        to: email,
        otp: kod,
      });

      setShowOtpValidation(false);
      handleKayitFinish();
      setOtpValidatingStatus(false);
      setCustomerOtp("");
    } catch (err) {
      setOtpValidatingStatus(false);
      toast.error(getError(err));
    }
  };

  useEffect(() => {
    if (userInfo) {
      navigate(redirect);
    }
  }, [navigate, redirect, userInfo]);

  return (
    <Container className="small-container signup-container">
      <Helmet>
        <title>Ücretsiz İşletme Üyeliği</title>
      </Helmet>
      <h1 className="signup-title">Ücretsiz İşletme Üyeliği</h1>
      <Form onSubmit={submitHandler} noValidate validated={formValidated}>
        <Form.Group className="mb-3">
          <Form.Label>Adınız</Form.Label>
          <Form.Control
            className="signup-form-control"
            onChange={(e) => {
              setName(capitalize(e.target.value));
            }}
            value={name}
            required
            autoFocus
          />
          <Form.Control.Feedback type="invalid">
            Lütfen ad alanini boş bırakmayınız
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>İşletme Adı</Form.Label>
          <Form.Control
            className="signup-form-control"
            onChange={(e) => setBusinessName(e.target.value)}
            value={businessName}
            required
          />
          <Form.Control.Feedback type="invalid">
            Lütfen işletme adı alanini boş bırakmayınız
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>İşletme Tipi</Form.Label>
          <Form.Select
            className="signup-form-control"
            value={businessType}
            required
            onChange={(e) => {
              setBusinessType(e.target.value);
            }}
          >
            <option value="" key="first">
              Seç
            </option>
            {businessTypes.map((c) => (
              <option value={c.type} key={c.type}>
                {c.name}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            Lütfen işletme tipi alanını boş bırakmayınız
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>E-posta Adresiniz</Form.Label>
          <Form.Control
            className="signup-form-control"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            pattern="^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
          />
          <Form.Control.Feedback type="invalid">
            Lütfen geçerli bir e-posta adresi giriniz
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Şifre</Form.Label>
          <Form.Control
            className="signup-form-control"
            type="password"
            required
            value={password}
            onChange={(e) => {
              const newPassword = e.target.value;
              setPassword(newPassword);
              validatePassword(newPassword, setPasswordStrength);
            }}
          />
          <small
            id="passwordHelpBlock"
            className={`signup-form-text ${
              passwordStrength === "Şifre güçlü"
                ? "text-success"
                : "text-danger"
            }`}
          >
            {passwordStrength}
          </small>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Şifrenizi Tekrar Ediniz</Form.Label>
          <Form.Control
            className="signup-form-control"
            type="password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            value={confirmPassword}
            required
          />
          <Form.Control.Feedback type="invalid">
            Lütfen şifre alanini boş bırakmayınız
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3">
          <button
            className="d-flex align-items-center mb-3 signup-form-control term-button"
            onClick={(e) => {
              e.preventDefault();
              if (!termsAccepted) {
                setShowTermsModal(true);
              }
              setTermsAccepted(!termsAccepted);
            }}
          >
            <Form.Check
              type="checkbox"
              id="termsCheckbox"
              className="signup-pointer-cursor"
              style={{ cursor: "pointer" }}
              value={termsAccepted}
              checked={termsAccepted}
              label="Kullanım koşullarını kabul ediyorum"
              onChange={(e) => {
                setShowTermsModal(e.target.checked);
                setTermsAccepted(e.target.checked);
              }}
            />
          </button>
        </Form.Group>
        <Form.Group className="mb-3">
          <button
            className="mb-3 signup-form-control term-button"
            onClick={(e) => {
              e.preventDefault();
              if (!privacyStatementAccepted) {
                setShowPrivacyStatement(true);
              }
              setPrivacyStatementAccepted(!privacyStatementAccepted);
            }}
          >
            <Form.Check
              type="checkbox"
              id="privacyStatement"
              className="signup-pointer-cursor"
              value={privacyStatementAccepted}
              checked={privacyStatementAccepted}
              label="Kişisel verilerimin işlenmesine yönelik Aydınlatma Metnini okudum ve anladım!"
              onChange={(e) => {
                setShowPrivacyStatement(e.target.checked);
                setPrivacyStatementAccepted(e.target.checked);
              }}
            />
          </button>
        </Form.Group>
        <div className="mb-3">
          <Button
            className="signup-button"
            type="submit"
            disabled={loadingCreate}
            size="lg"
          >
            {loadingCreate ? (
              <>
                <Spinner
                  style={{
                    display: "inline-block",
                    margin: "0px",
                    padding: "0px",
                    verticalAlign: "middle",
                  }}
                  animation="border"
                  role="status"
                  className="text-primary"
                ></Spinner>
                &nbsp; Kaydediliyor...
              </>
            ) : (
              <span>Bir aylık ücretsiz üyeliğini başlat!</span>
            )}
          </Button>{" "}
        </div>
        <div className="mb-3">
          Zaten bir hesabınız var mı?{" "}
          <Link to={`/signin?redirect=${redirect}`}>Giriş Yap</Link>
        </div>
      </Form>

      <Modal
        show={showTermsModal}
        onHide={() => setShowTermsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Kullanım Koşulları</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TermsOfService />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTermsModal(false)}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showPrivacyStatement}
        onHide={() => setShowPrivacyStatement(false)}
        size="lg"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <PrivacyStatement />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPrivacyStatement(false)}
          >
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      <OtpValidationModal
        showOtpValidation={showOtpValidation}
        setShowOtpValidation={setShowOtpValidation}
        handleValidateOtp={handleValidateOtp}
        setCustomerOtp={setCustomerOtp}
        customerOtp={customerOtp}
        setOtpValidatingStatus={setOtpValidatingStatus}
        otpValidatingStatus={otpValidatingStatus}
        email={email}
      />
    </Container>
  );
}
